import { useMemo, useCallback } from 'react'
import { min } from 'lodash-es'
import { Utils } from '@/utils/Utils'

export const DEFAULT_PAGE_SIZE_OPTIONS = [
  { label: '10개씩', value: 10 },
  { label: '50개씩', value: 50 },
  { label: '100개씩', value: 100 },
  { label: '500개씩', value: 500 },
  { label: '1,000개씩', value: 1000 },
  { label: '2,000개씩', value: 2000 },
] as const

export interface UsePaginationProps {
  totalCount: number
  currentPage: number
  pageSize: number
  pageSizeOptions?: ReadonlyArray<{ label: string; value: number }> | Array<{ label: string; value: number }>
  onChangePage?: (page: number) => void | Promise<void>
  onPageSizeChange?: (size: number) => void
}

export interface PaginationResult {
  totalCount: number
  currentPage: number
  totalPages: number
  rangeText: string
  pageSize: number
  pageSizeOptions: ReadonlyArray<{ label: string; value: number }> | Array<{ label: string; value: number }>
  isAvailablePrevPage: boolean
  isAvailableNextPage: boolean
  onPrevPage: () => void | Promise<void>
  onNextPage: () => void | Promise<void>
  onPageSizeChange: (size: number) => void | Promise<void>
}

export const usePagination = ({
  totalCount,
  currentPage,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onChangePage,
  onPageSizeChange,
}: UsePaginationProps): PaginationResult => {
  const totalPages = useMemo(() => Math.ceil(totalCount / pageSize), [totalCount, pageSize])

  const { rangeText, isAvailablePrevPage, isAvailableNextPage } = useMemo(() => {
    const start = currentPage * pageSize + 1
    const end = min([(currentPage + 1) * pageSize, totalCount]) || 0

    return {
      rangeText: pageSize && totalPages ? `${Utils.getCommaNum(start)}-${Utils.getCommaNum(end)}` : '',
      isAvailablePrevPage: currentPage <= 0,
      isAvailableNextPage: currentPage >= totalPages - 1,
    }
  }, [currentPage, totalCount, pageSize, totalPages])

  const handlePrevPage = useCallback(async () => {
    if (!isAvailablePrevPage) await onChangePage?.(currentPage - 1)
  }, [isAvailablePrevPage, currentPage, onChangePage])

  const handleNextPage = useCallback(async () => {
    if (!isAvailableNextPage) await onChangePage?.(currentPage + 1)
  }, [isAvailableNextPage, currentPage, onChangePage])

  const handlePageSizeChange = useCallback(
    async (size: number) => {
      onPageSizeChange?.(size)
      await onChangePage?.(0) // 페이지 크기 변경 시 첫 페이지로
    },
    [onChangePage, onPageSizeChange],
  )

  return {
    totalCount,
    currentPage,
    totalPages,
    rangeText,
    pageSize,
    pageSizeOptions,
    isAvailablePrevPage,
    isAvailableNextPage,
    onPrevPage: handlePrevPage,
    onNextPage: handleNextPage,
    onPageSizeChange: handlePageSizeChange,
  }
}
